import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const NONE_VALUE = "__none__";

export function NewProjectDialog({ open, onOpenChange, projects = [], onCreated }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: "", slug: "", description: "",
    domain: "", emergent_job_id: "", parent_project_id: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => setForm({
    name: "", slug: "", description: "",
    domain: "", emergent_job_id: "", parent_project_id: "",
  });

  const autoSlug = (name) => name
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

  const submit = async () => {
    const slug = form.slug.trim() || autoSlug(form.name);
    if (!form.name.trim() || !slug) {
      toast.error("Name and slug are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug,
        description: form.description.trim(),
        domain: form.domain.trim(),
        emergent_job_id: form.emergent_job_id.trim(),
        parent_project_id: form.parent_project_id || null,
      };
      const { data } = await api.post("/projects", payload);
      toast.success(`Project "${data.project.name}" created.`);
      reset();
      onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["projects-mini"] });
      onCreated && onCreated(data.project);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent data-testid="new-project-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">New project</DialogTitle>
          <DialogDescription>Create a workspace. Agents will see it on their next poll.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="np-name">Name</Label>
            <Input
              id="np-name" data-testid="np-name"
              value={form.name}
              onChange={(e) => {
                const name = e.target.value;
                setForm((f) => ({
                  ...f, name,
                  slug: f.slug ? f.slug : autoSlug(name),
                }));
              }}
              placeholder="ADEC Dubai"
            />
          </div>
          <div>
            <Label htmlFor="np-slug">Slug</Label>
            <Input
              id="np-slug" data-testid="np-slug"
              value={form.slug}
              onChange={(e) => set("slug")(e.target.value)}
              placeholder="adec-dubai"
            />
            <p className="text-[11px] font-mono text-muted-foreground mt-1">
              lowercase a-z, 0-9, hyphens. 3-64 chars.
            </p>
          </div>
          <div>
            <Label htmlFor="np-desc">Description</Label>
            <Textarea
              id="np-desc" data-testid="np-description" rows={2}
              value={form.description} onChange={(e) => set("description")(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="np-domain">Domain</Label>
              <Input id="np-domain" data-testid="np-domain"
                value={form.domain} onChange={(e) => set("domain")(e.target.value)}
                placeholder="adecdubai.ae" />
            </div>
            <div>
              <Label htmlFor="np-job">Emergent job ID</Label>
              <Input id="np-job" data-testid="np-emergent-job"
                value={form.emergent_job_id} onChange={(e) => set("emergent_job_id")(e.target.value)}
                placeholder="(optional)" />
            </div>
          </div>
          <div>
            <Label>Parent project</Label>
            <Select
              value={form.parent_project_id || NONE_VALUE}
              onValueChange={(v) => set("parent_project_id")(v === NONE_VALUE ? "" : v)}
            >
              <SelectTrigger data-testid="np-parent"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>— None (top-level) —</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" disabled={busy}
                  onClick={() => { reset(); onOpenChange(false); }}
                  data-testid="np-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="np-submit">
            {busy ? "Creating…" : "Create project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
